import { useCallback } from 'react'

import { useApolloClient, useMutation } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'

import { GetAccountConnectedApps } from './useConnectedApps'

export const RevokeAccessConnectedAppMutation = graphql(/* GraphQL */ `
  mutation revokeAccessConnectedApp($input: RevokeAccessConnectedAppInput!) {
    revokeAccessConnectedApp(input: $input) {
      __typename
      ... on EmptySuccess {
        _empty
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

type RevokeAccessConnectedApp = ({
  clientId,
}: {
  clientId: string
}) => Promise<void>

type RevokeAccessConnectedAppResponse = {
  revokeAccessConnectedApp: RevokeAccessConnectedApp
  isLoading: boolean
  hasError: boolean
}

/**
 * Custom hook to revoke connected apps' access to the account.
 *
 * @returns {RevokeAccessConnectedAppResponse} The function to trigger revoking access, with loading and error state.
 */
export const useRevokeApp = (): RevokeAccessConnectedAppResponse => {
  const client = useApolloClient()
  const [executeRevokeAccessConnectedApp, { loading, error }] = useMutation(
    RevokeAccessConnectedAppMutation,
  )

  const revokeAccessConnectedApp = useCallback(
    async ({ clientId }: { clientId: string }): Promise<void> => {
      let failed = false

      try {
        const { data, error: mutationError } =
          await executeRevokeAccessConnectedApp({
            variables: {
              input: {
                clientId,
              },
            },
          })

        if (data?.revokeAccessConnectedApp?.__typename === 'UnexpectedError') {
          throw new Error(data.revokeAccessConnectedApp.message)
        }

        if (
          mutationError ||
          data?.revokeAccessConnectedApp?.__typename !== 'EmptySuccess'
        ) {
          throw new Error(mutationError?.message || 'Unexpected API response')
        }
      } catch (thrown) {
        failed = true
        logError(thrown instanceof Error ? thrown : new Error(String(thrown)), {
          metaData: { variables: { clientId } },
        })
      }

      // A failed refresh is swallowed rather than reported: the toast below
      // reports the revoke either way, and Apollo keeps the previously loaded
      // list rather than blanking it, so nothing reads as revoked when it is
      // not.
      await client
        .refetchQueries({ include: [GetAccountConnectedApps] })
        .catch(() => undefined)

      if (failed) {
        toast.error('Unable to revoke access. Please try again later.')
        return
      }

      toast.success('We’ve revoked the access of the app')
    },
    [client, executeRevokeAccessConnectedApp],
  )

  return {
    revokeAccessConnectedApp,
    isLoading: loading,
    hasError: !!error,
  }
}
